import React from 'react';

/**
 * Inline SVG hero illustration — a stylized "people connecting" graphic.
 * No external assets, theme-aware.
 */
export const HeroIllustration = ({ size = 400, opacity = 1 }) => (
  <svg viewBox="0 0 480 360" width={size} height={(size * 360) / 480}
    style={{ opacity, maxWidth: '100%', height: 'auto' }}>
    <defs>
      <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3498DB" stopOpacity="0.15" />
        <stop offset="100%" stopColor="#1B4F72" stopOpacity="0.05" />
      </linearGradient>
      <linearGradient id="cardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F0F7FF" />
      </linearGradient>
      <linearGradient id="accentGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#F39C12" />
        <stop offset="100%" stopColor="#E67E22" />
      </linearGradient>
    </defs>

    {/* Background blob */}
    <ellipse cx="240" cy="200" rx="200" ry="140" fill="url(#bgGrad)" />

    {/* Dotted decoration */}
    <g fill="#F39C12" opacity="0.4">
      <circle cx="70" cy="80" r="4" />
      <circle cx="90" cy="60" r="3" />
      <circle cx="60" cy="50" r="2" />
      <circle cx="420" cy="280" r="4" />
      <circle cx="400" cy="300" r="3" />
      <circle cx="440" cy="260" r="2" />
    </g>

    {/* Floating cards (left = freelancer, right = client) */}
    <g>
      {/* Card 1 — Freelancer profile */}
      <rect x="60" y="120" width="160" height="140" rx="14" fill="url(#cardGrad)"
        stroke="#1B4F72" strokeWidth="1.5" opacity="0.95" />
      <circle cx="100" cy="155" r="20" fill="#1B4F72" />
      <text x="100" y="161" textAnchor="middle" fontSize="18" fontWeight="700" fill="white">A</text>
      <rect x="130" y="142" width="70" height="8" rx="4" fill="#34495E" />
      <rect x="130" y="156" width="50" height="6" rx="3" fill="#94A3B8" />
      <g transform="translate(75, 195)">
        <rect width="40" height="20" rx="10" fill="#FFB400" opacity="0.2" />
        <text x="20" y="14" textAnchor="middle" fontSize="11" fontWeight="700" fill="#B8860B">⭐ 4.9</text>
      </g>
      <g transform="translate(120, 195)">
        <rect width="80" height="20" rx="10" fill="#16C09B" opacity="0.2" />
        <text x="40" y="14" textAnchor="middle" fontSize="10" fontWeight="700" fill="#0E7A60">موثّق ✓</text>
      </g>
      <rect x="75" y="225" width="130" height="6" rx="3" fill="#E5E7EB" />
      <rect x="75" y="237" width="80" height="6" rx="3" fill="#E5E7EB" />
    </g>

    <g>
      {/* Card 2 — Project posting */}
      <rect x="260" y="100" width="160" height="140" rx="14" fill="url(#cardGrad)"
        stroke="#16A085" strokeWidth="1.5" opacity="0.95" />
      <rect x="275" y="115" width="100" height="10" rx="5" fill="#1B4F72" />
      <rect x="275" y="132" width="130" height="6" rx="3" fill="#94A3B8" />
      <rect x="275" y="145" width="120" height="6" rx="3" fill="#94A3B8" />
      <g transform="translate(275, 165)">
        <rect width="60" height="22" rx="11" fill="url(#accentGrad)" />
        <text x="30" y="16" textAnchor="middle" fontSize="11" fontWeight="700" fill="white">$500</text>
      </g>
      <g transform="translate(340, 165)">
        <rect width="60" height="22" rx="11" fill="#3498DB" opacity="0.85" />
        <text x="30" y="16" textAnchor="middle" fontSize="11" fontWeight="700" fill="white">14 يوم</text>
      </g>
      <circle cx="290" cy="215" r="10" fill="#9B59B6" />
      <circle cx="310" cy="215" r="10" fill="#16C09B" />
      <circle cx="330" cy="215" r="10" fill="#E67E22" />
      <text x="358" y="220" fontSize="11" fill="#5D6D7E">+5</text>
    </g>

    {/* Connection line / handshake */}
    <path d="M 220 200 Q 240 170 260 180" stroke="#F39C12" strokeWidth="3" fill="none" strokeDasharray="6 4" />

    {/* Wallet / coin icons floating */}
    <g transform="translate(360, 50)">
      <circle r="22" fill="url(#accentGrad)" />
      <text x="0" y="6" textAnchor="middle" fontSize="20" fill="white" fontWeight="700">$</text>
    </g>

    <g transform="translate(110, 290)">
      <circle r="18" fill="#16C09B" />
      <text x="0" y="5" textAnchor="middle" fontSize="16" fill="white" fontWeight="700">✓</text>
    </g>
  </svg>
);

/**
 * Hero illustration for the Categories page — stylized grid of category icons.
 */
export const CategoriesIllustration = ({ size = 300 }) => (
  <svg viewBox="0 0 360 240" width={size} height={(size * 240) / 360}
    style={{ maxWidth: '100%', height: 'auto', opacity: 0.92 }}>
    <defs>
      <linearGradient id="cBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3498DB" /><stop offset="100%" stopColor="#1B4F72" />
      </linearGradient>
      <linearGradient id="cOrange" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F39C12" /><stop offset="100%" stopColor="#E67E22" />
      </linearGradient>
      <linearGradient id="cGreen" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1ABC9C" /><stop offset="100%" stopColor="#16A085" />
      </linearGradient>
      <linearGradient id="cPurple" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#9B59B6" /><stop offset="100%" stopColor="#8E44AD" />
      </linearGradient>
    </defs>

    {/* 6 floating category tiles */}
    <g>
      <rect x="40" y="40" width="80" height="80" rx="16" fill="url(#cBlue)" />
      <text x="80" y="92" textAnchor="middle" fontSize="40" fill="white">💻</text>
    </g>
    <g>
      <rect x="140" y="20" width="80" height="80" rx="16" fill="url(#cOrange)" />
      <text x="180" y="72" textAnchor="middle" fontSize="40" fill="white">🎨</text>
    </g>
    <g>
      <rect x="240" y="50" width="80" height="80" rx="16" fill="url(#cGreen)" />
      <text x="280" y="102" textAnchor="middle" fontSize="40" fill="white">📝</text>
    </g>
    <g>
      <rect x="40" y="140" width="80" height="80" rx="16" fill="url(#cPurple)" />
      <text x="80" y="192" textAnchor="middle" fontSize="40" fill="white">📈</text>
    </g>
    <g>
      <rect x="140" y="120" width="80" height="80" rx="16" fill="url(#cOrange)" />
      <text x="180" y="172" textAnchor="middle" fontSize="40" fill="white">🎬</text>
    </g>
    <g>
      <rect x="240" y="150" width="80" height="80" rx="16" fill="url(#cBlue)" />
      <text x="280" y="202" textAnchor="middle" fontSize="40" fill="white">📊</text>
    </g>
  </svg>
);

export default HeroIllustration;
